// src/services/StorageService.js - COMPLETE FILE WITH ALL INVOICE METHODS

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebaseConfig';
import PasswordService from './PasswordService';
import AuthService from '../services/AuthService';

// Cache layer
let cachedData = null;
let cacheTimestamp = null;
const CACHE_DURATION = 30000; // 30 seconds

const StorageService = {
  isInitialized() {
    if (!db) {
      console.error('❌ Firestore is not initialized!');
      return false;
    }
    return true;
  },

  clearCache() {
    cachedData = null;
    cacheTimestamp = null;
     
  },

  getCachedData() {
    const now = Date.now();
    if (cachedData && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
       
      return { ...cachedData };
    }
    return null;
  },

  // ===================== INITIALIZATION WITH HASHED PASSWORDS =====================
  
  async init() {
  if (!this.isInitialized()) {
    console.error('❌ Cannot initialize - Firebase not ready');
    return;
  }

  try {
     
    
    const superadminRef = doc(db, 'superadmin', 'admin');
    const superadminDoc = await getDoc(superadminRef);
    
    if (!superadminDoc.exists()) {
       
      
      // ✅ YOUR EMAIL - Change this to your actual email
      const SUPER_ADMIN_EMAIL = 'mdsaquibsalim687@gmail.com';
      const SUPER_ADMIN_PASSWORD = 'Admin@12345'; // Change this to a strong password
      
      // ✅ Create Firebase Auth user for superadmin first
      const authResult = await AuthService.createUser(
        SUPER_ADMIN_EMAIL,
        SUPER_ADMIN_PASSWORD,
        { role: 'superadmin' }
      );

      if (!authResult.success) {
        console.error('❌ Failed to create superadmin auth user:', authResult.message);
        throw new Error('Failed to initialize superadmin: ' + authResult.message);
      }

      // ✅ Store superadmin without password
      await setDoc(superadminRef, {
        username: 'admin',
        email: SUPER_ADMIN_EMAIL,
        firebaseUid: authResult.uid,
        image: '',
        createdAt: new Date().toISOString(),
        authProvider: 'firebase'
      });
       

      // Create packages
      const packages = [
        { 
          id: 'pkg_001', 
          name: 'Basic', 
          price: 499, 
          features: ['50 Menu Items', '5GB Storage'], 
          qrLimit: 10 
        },
        { 
          id: 'pkg_002', 
          name: 'Premium', 
          price: 999, 
          features: ['200 Menu Items', '20GB Storage', 'Analytics'], 
          qrLimit: 20 
        },
        { 
          id: 'pkg_003', 
          name: 'Advanced', 
          price: 1999, 
          features: ['Unlimited Menu', '100GB Storage', 'Advanced Analytics', 'Priority Support'], 
          qrLimit: 30 
        }
      ];

      for (const pkg of packages) {
        await setDoc(doc(db, 'packages', pkg.id), pkg);
      }
       

      // ⚠️ OPTIONAL: Create demo restaurant (comment out if you don't need it)
      /*
      const restaurantAuthResult = await AuthService.createUser(
        'royal@example.com',
        'Royal@123',
        { role: 'restaurant_owner' }
      );

      if (restaurantAuthResult.success) {
        const restaurantRef = doc(db, 'restaurants', 'rest_001');
        await setDoc(restaurantRef, {
          id: 'rest_001',
          firebaseUid: restaurantAuthResult.uid,
          name: 'Royal Spice',
          username: 'royalowner',
          email: 'royal@example.com',
          package: 'Premium',
          status: 'Active',
          categories: ['Main Course', 'Starters', 'Desserts', 'Beverages'],
          subcategories: ['Veg', 'Non-Veg', 'Vegan'],
          foodOfTheDay: null,
          address: 'Main Street, City Center',
          phone: '+91 9876543210',
          gstin: '22AAAAA0000A1Z5',
          invoiceCounter: 1,
          cgstPercentage: 0,
          sgstPercentage: 0,
          serviceChargePercentage: 0,
          isGSTRegistered: false,
          createdAt: new Date().toISOString(),
          authProvider: 'firebase'
        });
         
      }
      */
    } else {
       
    }
  } catch (error) {
   // console.error("");
  }
},

  // ===================== GET DATA =====================
  // Replace the getData() method in your StorageService.js with this:

async getData(forceRefresh = false) {
  if (!this.isInitialized()) {
    console.error('❌ Firebase not initialized');
    return {
      superadmin: { username: 'admin', password: '', image: '' },
      restaurants: [],
      packages: [],
      customerSessions: {}
    };
  }

  if (!forceRefresh) {
    const cached = this.getCachedData();
    if (cached) return cached;
  }

  try {
     
    
    const currentUser = AuthService.getCurrentUser();
    const currentEmail = currentUser?.email;
     

    // Fetch superadmin
    const superadminRef = doc(db, 'superadmin', 'admin');
    let superadminDoc;
    try {
      superadminDoc = await getDoc(superadminRef);
    } catch (error) {
      //console.warn('⚠️ Could not fetch superadmin (permission denied):', error.code);
      superadminDoc = null;
    }

    const superadmin = superadminDoc?.exists() ? superadminDoc.data() : null;

    // Fetch packages, sessions
    const [packagesSnapshot, sessionsSnapshot] = await Promise.all([
      getDocs(collection(db, 'packages')).catch(err => {
        console.warn('⚠️ Could not fetch packages:', err.code);
        return { docs: [] };
      }),
      getDocs(collection(db, 'sessions')).catch(err => {
        console.warn('⚠️ Could not fetch sessions:', err.code);
        return { docs: [] };
      })
    ]);

    const packages = packagesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const customerSessions = {};
    sessionsSnapshot.docs.forEach(doc => {
      customerSessions[doc.id] = { id: doc.id, ...doc.data() };
    });

    let restaurants = [];

    // ✅ Check if user is superadmin
    const isSuperAdmin = superadmin && currentEmail === superadmin.email;

    if (isSuperAdmin) {
      // ✅ Super Admin: Fetch ALL restaurants
       
      try {
        const restaurantsSnapshot = await getDocs(collection(db, 'restaurants'));
         

        const restaurantPromises = restaurantsSnapshot.docs.map(async (restaurantDoc) => {
          const restaurant = { id: restaurantDoc.id, ...restaurantDoc.data() };

          const [menuSnapshot, ordersSnapshot, tablesSnapshot] = await Promise.all([
            getDocs(collection(db, 'restaurants', restaurant.id, 'menu')),
            getDocs(collection(db, 'restaurants', restaurant.id, 'orders')),
            getDocs(collection(db, 'restaurants', restaurant.id, 'tables'))
          ]);

          restaurant.menu = menuSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          restaurant.orders = ordersSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              time: data.time?.toDate?.()?.toISOString() || data.time || new Date().toISOString()
            };
          });

          restaurant.tables = tablesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          return restaurant;
        });

        restaurants = await Promise.all(restaurantPromises);
      } catch (error) {
        console.error('❌ Error fetching restaurants for admin:', error);
        restaurants = [];
      }
    } else if (currentEmail) {
      // ✅ Restaurant Owner: Query for THEIR restaurant by email
       
      
      try {
        // We can't query by email due to permissions, so we try to fetch all and filter
        // But since we can't list all, we need to try a different approach
        const restaurantsSnapshot = await getDocs(collection(db, 'restaurants'));
         

        const restaurantPromises = restaurantsSnapshot.docs.map(async (restaurantDoc) => {
          const restaurant = { id: restaurantDoc.id, ...restaurantDoc.data() };

          // Only include restaurants matching current user's email
          if (restaurant.email !== currentEmail) {
            return null;
          }

          const [menuSnapshot, ordersSnapshot, tablesSnapshot] = await Promise.all([
            getDocs(collection(db, 'restaurants', restaurant.id, 'menu')),
            getDocs(collection(db, 'restaurants', restaurant.id, 'orders')),
            getDocs(collection(db, 'restaurants', restaurant.id, 'tables'))
          ]);

          restaurant.menu = menuSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          restaurant.orders = ordersSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              time: data.time?.toDate?.()?.toISOString() || data.time || new Date().toISOString()
            };
          });

          restaurant.tables = tablesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          return restaurant;
        });

        const allRestaurants = await Promise.all(restaurantPromises);
        restaurants = allRestaurants.filter(r => r !== null);
        
         
      } catch (error) {
        console.error('❌ Error fetching restaurant for owner:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        restaurants = [];
      }
    }

    const data = {
      superadmin,
      restaurants,
      packages,
      customerSessions
    };

    cachedData = data;
    cacheTimestamp = Date.now();

    // console.log(`:`, {
    //   superadmin: superadmin?.username || 'none',
    //   restaurants: restaurants.length,
    //   restaurantEmails: restaurants.map(r => r.email),
    //   packages: packages.length,
    //   sessions: Object.keys(customerSessions).length
    // });

    return data;
  } catch (error) {
    console.error('❌ getData error:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    
    return {
      superadmin: { username: 'admin', password: '', image: '' },
      restaurants: [],
      packages: [],
      customerSessions: {}
    };
  }
},

  async getRestaurant(restaurantId) {
    if (!this.isInitialized()) return null;

    try {
      const cached = this.getCachedData();
      if (cached) {
        const restaurant = cached.restaurants.find(r => r.id === restaurantId);
        if (restaurant) {
           
          return { ...restaurant };
        }
      }

       
      const restaurantRef = doc(db, 'restaurants', restaurantId);
      const restaurantDoc = await getDoc(restaurantRef);

      if (!restaurantDoc.exists()) return null;

      const restaurant = { id: restaurantDoc.id, ...restaurantDoc.data() };

      const [menuSnapshot, ordersSnapshot, tablesSnapshot] = await Promise.all([
        getDocs(collection(db, 'restaurants', restaurant.id, 'menu')),
        getDocs(collection(db, 'restaurants', restaurant.id, 'orders')),
        getDocs(collection(db, 'restaurants', restaurant.id, 'tables'))
      ]);

      restaurant.menu = menuSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      restaurant.orders = ordersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          time: data.time?.toDate?.()?.toISOString() || data.time || new Date().toISOString()
        };
      });
      restaurant.tables = tablesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return restaurant;
    } catch (error) {
      console.error('❌ getRestaurant error:', error);
      return null;
    }
  },

  async updateRestaurantCategories(restaurantId, categories, subcategories) {
    if (!this.isInitialized()) return;

    try {
      const restaurantRef = doc(db, 'restaurants', restaurantId);
      await updateDoc(restaurantRef, {
        categories,
        subcategories,
        updatedAt: new Date().toISOString()
      });

      if (cachedData) {
        const restaurant = cachedData.restaurants.find(r => r.id === restaurantId);
        if (restaurant) {
          restaurant.categories = categories;
          restaurant.subcategories = subcategories;
        }
      }

       
    } catch (error) {
      console.error('❌ updateRestaurantCategories error:', error);
      throw error;
    }
  },

  // ===================== SET DATA =====================
  
  async setData(data) {
    if (!this.isInitialized()) {
      console.error('❌ Firebase not initialized');
      return;
    }

    try {
       

      const batch = writeBatch(db);

      if (data.superadmin) {
        const superadminRef = doc(db, 'superadmin', 'admin');
        batch.set(superadminRef, data.superadmin, { merge: true });
      }

      if (data.packages && Array.isArray(data.packages)) {
        for (const pkg of data.packages) {
          const pkgRef = doc(db, 'packages', pkg.id);
          batch.set(pkgRef, pkg, { merge: true });
        }
      }

      await batch.commit();

      if (data.restaurants && Array.isArray(data.restaurants)) {
        for (const restaurant of data.restaurants) {
          const { menu, orders, tables, ...restaurantData } = restaurant;
          const restaurantRef = doc(db, 'restaurants', restaurant.id);
          
          await setDoc(restaurantRef, {
            ...restaurantData,
            updatedAt: new Date().toISOString()
          }, { merge: true });

          if (menu && Array.isArray(menu)) {
            for (const item of menu) {
              const menuRef = doc(db, 'restaurants', restaurant.id, 'menu', item.id);
              await setDoc(menuRef, item, { merge: true });
            }
          }

          if (orders && Array.isArray(orders)) {
            for (const order of orders) {
              const orderData = { ...order };
              if (orderData.time instanceof Date) {
                orderData.time = orderData.time.toISOString();
              }
              const orderRef = doc(db, 'restaurants', restaurant.id, 'orders', order.id);
              await setDoc(orderRef, orderData, { merge: true });
            }
          }

          if (tables && Array.isArray(tables)) {
            for (const table of tables) {
              const tableRef = doc(db, 'restaurants', restaurant.id, 'tables', table.id);
              await setDoc(tableRef, table, { merge: true });
            }
          }
        }
      }

      cachedData = data;
      cacheTimestamp = Date.now();

       
    } catch (error) {
      console.error('❌ setData error:', error);
      throw error;
    }
  },

  // ===================== SESSION MANAGEMENT =====================
  
  async createSession(orderId, restaurantId, tableId) {
    if (!this.isInitialized()) {
      return `session_${Date.now()}`;
    }

    try {
      const sessionId = `session_${Date.now()}`;
      const sessionRef = doc(db, 'sessions', sessionId);
      
      const sessionData = {
        id: sessionId,
        orderId,
        restaurantId,
        tableId: tableId || null,
        createdAt: new Date().toISOString(),
        active: true
      };
      
      await setDoc(sessionRef, sessionData);

      if (cachedData) {
        cachedData.customerSessions[sessionId] = sessionData;
      }

       
      return sessionId;
    } catch (error) {
      console.error('❌ createSession error:', error);
      return `session_${Date.now()}`;
    }
  },

  async getSession(sessionId) {
    if (!this.isInitialized()) return null;

    try {
      if (cachedData && cachedData.customerSessions[sessionId]) {
         
        return { ...cachedData.customerSessions[sessionId] };
      }

      const sessionRef = doc(db, 'sessions', sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (sessionDoc.exists()) {
        const session = { id: sessionDoc.id, ...sessionDoc.data() };
        
        if (cachedData) {
          cachedData.customerSessions[sessionId] = session;
        }
        
        return session;
      }
      return null;
    } catch (error) {
      console.error('❌ getSession error:', error);
      return null;
    }
  },

  async endSession(sessionId) {
    if (!this.isInitialized()) return;

    try {
      const sessionRef = doc(db, 'sessions', sessionId);
      await updateDoc(sessionRef, {
        active: false,
        endedAt: new Date().toISOString()
      });

      if (cachedData && cachedData.customerSessions[sessionId]) {
        cachedData.customerSessions[sessionId].active = false;
        cachedData.customerSessions[sessionId].endedAt = new Date().toISOString();
      }

       
    } catch (error) {
      console.error('❌ endSession error:', error);
    }
  },

  // ===================== BILLING & INVOICE METHODS =====================

  /**
   * Save invoice to Firestore
   */
  async saveInvoice(restaurantId, invoice) {
    if (!this.isInitialized()) {
      throw new Error('Firebase not initialized');
    }

    try {
      const invoiceRef = doc(db, 'restaurants', restaurantId, 'invoices', invoice.invoiceNumber);
      await setDoc(invoiceRef, invoice);
      
       
      return invoice.invoiceNumber;
    } catch (error) {
      console.error('❌ saveInvoice error:', error);
      throw error;
    }
  },

  /**
   * Get all invoices for a restaurant with optional filters
   */
  async getInvoices(restaurantId, filters = {}) {
    if (!this.isInitialized()) return [];

    try {
      const invoicesSnapshot = await getDocs(
        collection(db, 'restaurants', restaurantId, 'invoices')
      );
      
      let invoices = invoicesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Apply filters if provided
      if (filters.includeCancelled === false) {
        invoices = invoices.filter(inv => !inv.cancelled);
      }

      if (filters.printed !== undefined) {
        invoices = invoices.filter(inv => inv.printed === filters.printed);
      }

      // Sort by timestamp (newest first)
      invoices.sort((a, b) => {
        const dateA = new Date(a.timestamp);
        const dateB = new Date(b.timestamp);
        return dateB - dateA;
      });
      
       
      return invoices;
    } catch (error) {
      console.error('❌ getInvoices error:', error);
      return [];
    }
  },

  /**
   * Get invoices within a specific date range
   */
  async getInvoicesByDateRange(restaurantId, startDate, endDate) {
    if (!this.isInitialized()) return [];

    try {
      const allInvoices = await this.getInvoices(restaurantId, { 
        includeCancelled: false
      });
      
      const filtered = allInvoices.filter(invoice => {
        const invoiceDate = new Date(invoice.timestamp);
        return invoiceDate >= startDate && invoiceDate <= endDate;
      });
      
       
      return filtered;
    } catch (error) {
      console.error('❌ getInvoicesByDateRange error:', error);
      return [];
    }
  },

  /**
   * Get a specific invoice
   */
  async getInvoice(restaurantId, invoiceNumber) {
    if (!this.isInitialized()) return null;

    try {
      const invoiceRef = doc(
        db,
        'restaurants',
        restaurantId,
        'invoices',
        invoiceNumber
      );
      
      const invoiceDoc = await getDoc(invoiceRef);
      
      if (invoiceDoc.exists()) {
         
        return { id: invoiceDoc.id, ...invoiceDoc.data() };
      }
      
       
      return null;
    } catch (error) {
      console.error('❌ getInvoice error:', error);
      return null;
    }
  },

  /**
   * Mark invoice as printed
   */
  async markInvoiceAsPrinted(restaurantId, invoiceNumber) {
    if (!this.isInitialized()) return;

    try {
      const invoiceRef = doc(
        db,
        'restaurants',
        restaurantId,
        'invoices',
        invoiceNumber
      );
      
      await updateDoc(invoiceRef, {
        printed: true,
        printedAt: new Date().toISOString()
      });
      
      this.clearCache();
       
    } catch (error) {
      console.error('❌ markInvoiceAsPrinted error:', error);
      throw error;
    }
  },

  /**
   * Cancel an invoice (mark as cancelled and revert orders)
   */
  async cancelInvoice(restaurantId, invoiceNumber, reason, cancelledBy = 'System') {
    if (!this.isInitialized()) {
      throw new Error('Firebase not initialized');
    }

    try {
      const invoiceRef = doc(
        db,
        'restaurants',
        restaurantId,
        'invoices',
        invoiceNumber
      );

      const invoiceDoc = await getDoc(invoiceRef);
      if (!invoiceDoc.exists()) {
        throw new Error('Invoice not found');
      }

      const invoice = invoiceDoc.data();
      const now = new Date().toISOString();

      // Update invoice
      await updateDoc(invoiceRef, {
        cancelled: true,
        cancellationReason: reason,
        cancelledAt: now,
        cancelledBy: cancelledBy,
        status: 'Cancelled'
      });

      // Revert orders to unbilled status
      if (invoice.orderIds && Array.isArray(invoice.orderIds)) {
        for (const orderId of invoice.orderIds) {
          try {
            const orderRef = doc(
              db,
              'restaurants',
              restaurantId,
              'orders',
              orderId
            );

            await updateDoc(orderRef, {
              billed: false,
              invoiceNumber: null,
              billedAt: null
            });
          } catch (orderError) {
            console.warn(`Could not revert order ${orderId}:`, orderError);
          }
        }
      }

      this.clearCache();
       
      
      return {
        success: true,
        message: 'Invoice cancelled successfully',
        cancelledAt: now
      };
    } catch (error) {
      console.error('❌ cancelInvoice error:', error);
      throw error;
    }
  },

  /**
   * Get cancellation statistics
   */
  async getCancellationStats(restaurantId, startDate = null, endDate = null) {
    if (!this.isInitialized()) {
      return { 
        totalCancelled: 0, 
        cancellationReasons: {},
        revenueImpact: 0 
      };
    }

    try {
      let invoices;
      
      if (startDate && endDate) {
        invoices = await this.getInvoicesByDateRange(restaurantId, startDate, endDate);
      } else {
        invoices = await this.getInvoices(restaurantId);
      }

      const cancellations = invoices.filter(inv => inv.cancelled);
      
      const reasonCounts = {};
      let revenueImpact = 0;

      cancellations.forEach(inv => {
        const reason = inv.cancellationReason || 'Unknown';
        reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
        revenueImpact += inv.grandTotal;
      });

      return {
        totalCancelled: cancellations.length,
        totalInvoices: invoices.length,
        cancellationRate: invoices.length > 0 ? (cancellations.length / invoices.length * 100).toFixed(2) : 0,
        cancellationReasons: reasonCounts,
        revenueImpact: revenueImpact,
        averageAmount: cancellations.length > 0 ? revenueImpact / cancellations.length : 0
      };
    } catch (error) {
      console.error('❌ getCancellationStats error:', error);
      return { 
        totalCancelled: 0, 
        cancellationReasons: {},
        revenueImpact: 0 
      };
    }
  },

  /**
   * Get revenue statistics with GST breakdown
   */
  async getRevenueStats(restaurantId, startDate = null, endDate = null) {
    if (!this.isInitialized()) {
      return { 
        total: 0, 
        count: 0,
        subtotal: 0,
        cgst: 0,
        sgst: 0,
        serviceCharge: 0,
        taxes: 0,
        averageOrderValue: 0
      };
    }

    try {
      let invoices;
      
      if (startDate && endDate) {
        invoices = await this.getInvoicesByDateRange(restaurantId, startDate, endDate);
      } else {
        invoices = await this.getInvoices(restaurantId, { includeCancelled: false });
      }
      
      const stats = {
        total: 0,
        count: 0,
        subtotal: 0,
        cgst: 0,
        sgst: 0,
        serviceCharge: 0,
        taxes: 0,
        averageOrderValue: 0,
        printed: 0,
        pending: 0
      };
      
      invoices.forEach(inv => {
        stats.total += inv.grandTotal || 0;
        stats.subtotal += inv.subtotal || 0;
        stats.cgst += inv.cgst || 0;
        stats.sgst += inv.sgst || 0;
        stats.serviceCharge += inv.serviceCharge || 0;
        stats.taxes += inv.taxTotal || 0;
        stats.count++;
        if (inv.printed) stats.printed++;
        else stats.pending++;
      });
      
      if (stats.count > 0) {
        stats.averageOrderValue = stats.total / stats.count;
      }
      
       
      return stats;
    } catch (error) {
      console.error('❌ getRevenueStats error:', error);
      return { 
        total: 0, 
        count: 0,
        subtotal: 0,
        cgst: 0,
        sgst: 0,
        serviceCharge: 0,
        taxes: 0,
        averageOrderValue: 0,
        printed: 0,
        pending: 0
      };
    }
  },

  /**
   * Update invoice counter
   */
  async updateInvoiceCounter(restaurantId, newCounter) {
    if (!this.isInitialized()) return;

    try {
      const restaurantRef = doc(db, 'restaurants', restaurantId);
      await updateDoc(restaurantRef, {
        invoiceCounter: newCounter,
        updatedAt: new Date().toISOString()
      });

      if (cachedData) {
        const restaurant = cachedData.restaurants.find(r => r.id === restaurantId);
        if (restaurant) {
          restaurant.invoiceCounter = newCounter;
        }
      }

       
    } catch (error) {
      console.error('❌ updateInvoiceCounter error:', error);
      throw error;
    }
  },

  /**
   * Delete an invoice (admin only)
   */
  async deleteInvoice(restaurantId, invoiceNumber) {
    if (!this.isInitialized()) return;

    try {
      const invoiceRef = doc(db, 'restaurants', restaurantId, 'invoices', invoiceNumber);
      await deleteDoc(invoiceRef);
      
      this.clearCache();
       
    } catch (error) {
      console.error('❌ deleteInvoice error:', error);
      throw error;
    }
  }
};

export default StorageService;